import { NotificationAction } from "@config/local-notifications/notification-actions";
import { NotificationIcon } from "@config/local-notifications/notification-icon";
import {
  organizationManagerNotificationRepo,
  organizationManagerRepo,
} from "@repositories";
import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import { awaiter } from "@shared/helpers/awaiter";
import { textBuilder } from "@shared/helpers/text-builder";
import { OrganizationRoles, Roles, type BaseRoles } from "@shared/Roles";
import { Allow, remult } from "remult";
import { OrganizationManager } from "../organization-manager";
import { type Association } from "./entity";

export const AssociationEntityConfig: HyperEntityConfig<Association> = {
  allowApiRead: Allow.authenticated,
  allowApiCrud: [Roles.Admin, Roles.SuperAdmin, Roles.Manager, Roles.User],
  apiPrefilterPerRole: (filter) => {
    if (!remult.user) return;

    filter(Roles.SuperDuperAdmin, false);

    filter([Roles.Admin, Roles.SuperAdmin], {
    });

    filter(Roles.Manager, {
    });

    filter(Roles.User, {
      id: {
        $in: remult.user.associations,
      },
    });
  },

  allowInsertPerRole(allow) {
    allow(Roles.User, (entity) => entity.userId === remult.user!.id);

    allow(
      Roles.OrganizationManagers,
      (entity) => entity.organizationId === remult.user!.organizationId,
    );
  },

  allowDeleteAndUpdatePerRole(allow) {
    allow(Roles.User, (entity) => entity.userId === remult.user!.id);

    allow(
      [Roles.Admin, Roles.SuperAdmin],
      (entity) => entity.organizationId === remult.user!.organizationId!,
    );

    allow(Roles.Manager, {
      teams: {
        $some: {
          teamId: {
            $in: remult.user!.managingTeams,
          },
        },
      },
    });
  },
  async onInsert(entity, e) {
    if (entity.roles.includes(OrganizationRoles.Volunteer)) {
      const [user, organization] = await awaiter(
        e.relations.user.findOne(),
        e.relations.organization.findOne({
          include: {
            managers: true,
          },
        }),
      );

      if (!organization || !user) return;

      const admins = organization.managers.filter((v) =>
        v.roles.some((role) => Roles.Admins.includes(role as BaseRoles)),
      );

      const text = textBuilder()
        .if(user.gender === "MALE", "המתנדב")
        .if(user.gender === "FEMALE", "המתנדבת")
        .if(user.gender === "OTHER", "המתנדב/ת")
        .space()
        .add(user.name)
        .space()
        .if(user.gender === "MALE", "נרשם")
        .if(user.gender === "FEMALE", "נרשמה")
        .if(user.gender === "OTHER", "נרשמ/ה")
        .space()
        .add("לארגון שלך.")
        .done();

      await organizationManagerNotificationRepo.insert(
        admins.map((admin) => ({
          toId: admin.id,
          title: "מתנדב חדש",
          content: text,
          icon: NotificationIcon.USER_ADD,
          actions: [
            {
              btnText: "לפרטי המתנדב/ת",
              id: NotificationAction.NAVIGATE,
              extra: {
                to: `/volunteers/${entity.id}`,
              },
            },
          ],
        })),
      );
    }
  },

  async onDelete(entity, e) {
    if (entity.roles.includes(OrganizationRoles.Volunteer)) {
      const [user, organization, teamManagers] = await awaiter(
        e.relations.user.findOne(),
        e.relations.organization.findOne({
          include: {
            managers: true,
          },
        }),
        organizationManagerRepo.find({
          where: OrganizationManager.hyperFilter({
            teams: {
              $some: {
                team: {
                  $is: {
                    associations: {
                      $some: {
                        associationId: entity.id,
                      },
                    },
                  },
                },
              },
            },
          }),
        }),
      );

      if (!organization || !user) return;

      const admins = organization.managers.filter((v) =>
        v.roles.some((role) => Roles.Admins.includes(role as BaseRoles)),
      );

      const usersToSendTo = Array.from(
        new Set([...admins, ...teamManagers].map((v) => v.id)),
      );

      const text = textBuilder()
        .if(user.gender === "MALE", "המתנדב")
        .if(user.gender === "FEMALE", "המתנדבת")
        .if(user.gender === "OTHER", "המתנדב/ת")
        .space()
        .add(user.name)
        .space()
        .if(user.gender === "MALE", "הסיר")
        .if(user.gender === "FEMALE", "הסירה")
        .if(user.gender === "OTHER", "הסיר/ה")
        .space()
        .add("את")
        .space()
        .if(user.gender === "MALE", "הרשמתו")
        .if(user.gender === "FEMALE", "הרשמתה")
        .if(user.gender === "OTHER", "הרשמתו/ה")
        .space()
        .add("מהמערכת.")
        .newLine()
        .add("כל")
        .if(user.gender === "MALE", "פרטיו")
        .if(user.gender === "FEMALE", "פרטיה")
        .if(user.gender === "OTHER", "פרטיו/ה")
        .space()
        .add("נמחקו בהתאם למדיניות הפרטיות.")
        .done();

      await organizationManagerNotificationRepo.insert(
        usersToSendTo.map((id) => ({
          toId: id,
          title: "מתנדב נמחק",
          content: text,
          icon: NotificationIcon.USER_CANCELED,
          actions: [
            {
              btnText: "לכל המתנדבים",
              id: NotificationAction.NAVIGATE,
              extra: {
                to: `/volunteers`,
              },
            },
          ],
        })),
      );
    }
  },

  defaultOrderBy: {
    createdAt: "desc",
  },
};
